import { CircleX } from 'lucide-react'
import PropTypes from 'prop-types'
import { Fragment, useCallback, useEffect } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'

import { Button } from '@/components/elements/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/elements/form'
import { Input } from '@/components/elements/input'
import { Label } from '@/components/elements/label'
import { FileUploader } from '@/components/FileUploader'
import { Select } from '@/components/Select'
import { SlidePanel } from '@/components/SlidePanel'
import { cn } from '@/utils/helpers'

import QuestionAnswer from './QuestionAnswer'

const QuizQuestion = ({
  append,
  close,
  index,
  question = null,
  readOnly = false,
  remove,
  update,
}) => {
  // Form
  const form = useForm({
    defaultValues: {
      // Carry the question's database id through the panel. `update()` replaces
      // the whole field-array entry, so anything missing here is gone from the
      // payload — and a question submitted without an id is created fresh while
      // the original is deleted as an orphan (RSCX-385). Omitted entirely for a
      // new question so the backend still creates one.
      ...(question?.id ? { id: question.id } : {}),
      type: question?.type || '',
      text: question?.text || '',
      answers: question?.answers || [],
      imageFilePath: question?.imageFilePath || null,
    },
  })
  const singleOption = form.watch('type') === 'Numeric' || form.watch('type') === 'Drag and Drop'
  const {
    fields: answers,
    append: appendAnswer,
    move,
    remove: removeAnswer,
    replace,
    // update: updateAnswer,
  } = useFieldArray({
    control: form.control,
    name: 'answers',
    rules: {
      required: true,
      minLength: {
        value: singleOption ? 1 : 2,
        message: `Must have at least ${singleOption ? 1 : 2} option${singleOption ? '' : 's'}.`,
      },
    },
  })

  const hasAnswerControl =
    form.watch('type') !== 'Numeric' &&
    form.watch('type') !== 'True/False' &&
    form.watch('type') !== 'Drag and Drop'
  const orderAnswers = useCallback((dragIndex, hoverIndex) => move(dragIndex, hoverIndex), [move])
  const renderQuestionAnswer = useCallback(
    (answer, answerIndex) => (
      <QuestionAnswer
        answer={answer}
        questionAnswers={answers}
        key={answer.id}
        index={answerIndex}
        name={`answers.${answerIndex}`}
        orderAnswers={orderAnswers}
        questionType={form.watch('type')}
        readOnly={readOnly}
        remove={removeAnswer}
      />
    ),
    [answers, form, orderAnswers, readOnly, removeAnswer],
  )
  const watchType = form.watch('type')
  const watchImage = form.watch('imageFilePath')
  useEffect(() => {
    // Set default answers based on question type
    if (index === null) {
      if (watchType === 'Numeric') {
        replace([{ title: '', isCorrect: true, displayOrder: 0 }])
      } else if (watchType === 'True/False') {
        replace([
          { title: 'True', isCorrect: false, displayOrder: 0 },
          { title: 'False', isCorrect: false, displayOrder: 1 },
        ])
      } else if (form.watch('type') === 'Drag and Drop') {
        replace([{ type: 'text', value: '' }])
      }
    }
  }, [form, index, replace, watchType])

  const canRemove = index !== null && !readOnly

  const renderImage = () => {
    if (question?.signedImagePath && watchImage !== null) {
      return (
        <div className="flex flex-col flex-wrap items-start justify-between gap-2 sm:flex-row">
          <div>
            <Label>Image</Label>
            <img src={question?.signedImagePath} alt="" className="my-2 min-h-20 w-40" />
          </div>

          <Button
            type="button"
            variant="destructive"
            onClick={() => {
              form.setValue(`imageFilePath`, null)
            }}
          >
            Remove Image
          </Button>
        </div>
      )
    }
    if (!question?.imageFilePath || watchImage !== question?.imageFilePath) {
      return (
        <FormField
          control={form.control}
          name="imageFilePath"
          render={() => (
            <FormItem>
              <FormLabel>Question Image</FormLabel>
              <FormControl>
                <FileUploader
                  acceptedFileTypes={['image/*']}
                  handleUploadToServer={async (file) => {
                    form.setValue('imageFilePath', file.fileUploadName)
                  }}
                  id="file"
                  maxFiles={1}
                  type="aws"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )
    }

    return (
      <div className="flex flex-col items-start justify-between gap-2 sm:flex-row sm:items-center">
        <div>
          <Label>Image</Label>
          <p>{question.imageFilePath}</p>
          <small>Save the quiz to see the preview.</small>
        </div>

        <Button
          type="button"
          variant="destructive"
          onClick={() => {
            form.setValue(`imageFilePath`, null)
          }}
        >
          Remove Image
        </Button>
      </div>
    )
  }

  return (
    <SlidePanel
      actions={[
        {
          label: 'Cancel',
          onClick: () => close(),
          type: 'cancel',
        },
        {
          label: `${question ? 'Update' : 'Add'} Question`,
          onClick: form.handleSubmit((data) => {
            // Validate that at least one answer is marked as correct
            if (data.type !== 'Numeric' && data.type !== 'Drag and Drop') {
              const hasCorrectAnswer = data.answers.some((a) => a.isCorrect)
              if (!hasCorrectAnswer) {
                form.setError('answers', {
                  type: 'manual',
                  message: 'At least one answer must be marked as correct.',
                })
                return
              }
            }

            if (question !== null && index !== null) {
              update(index, data)
            } else {
              append(data)
            }

            close()
          }),
          type: 'submit',
        },
        ...(canRemove
          ? [
              {
                label: 'Remove Question',
                onClick: () => {
                  remove(index)
                  close()
                },
                type: 'delete',
              },
            ]
          : []),
      ]}
      content={
        <Form {...form}>
          <form className="flex w-full flex-col gap-4">
            <FormField
              control={form.control}
              name="type"
              render={({ field: questionField }) => (
                <FormItem>
                  <FormLabel>Type</FormLabel>
                  <FormControl>
                    <Select
                      id="type"
                      disabled={index !== null}
                      placeholder="Select Question Type"
                      options={[
                        { label: 'Multiple Choice', id: 'Multiple Choice' },
                        { label: 'Single Choice', id: 'Single Choice' },
                        { label: 'True/False', id: 'True/False' },
                        { label: 'Numeric', id: 'Numeric' },
                        { label: 'Drag and Drop', id: 'Drag and Drop' },
                      ]}
                      className="w-full"
                      {...questionField}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
              rules={{
                required: true,
              }}
            />
            {/* Non-drag and drop */}
            {form.watch('type') !== 'Drag and Drop' && (
              <FormField
                control={form.control}
                name="text"
                render={({ field: questionField }) => (
                  <FormItem>
                    <FormLabel>Question Text</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter question text" {...questionField} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
                rules={{
                  required: true,
                }}
              />
            )}

            {form.watch('type') && form.watch('type') !== 'Drag and Drop' && (
              <div className="flex flex-col gap-2">
                {form.watch('type') !== 'Numeric' ? (
                  <div className="flex flex-col">
                    <span
                      className={cn(
                        'text-sm font-semibold',
                        form.formState.errors.answers && 'text-destructive',
                      )}
                    >
                      Answer Options
                    </span>
                    <p className="text-muted-foreground text-xs">
                      Options will be displayed in the order below. You can drag and drop to
                      reorder them.
                    </p>

                    <FormMessage className="text-destructive text-xs">
                      {form.formState.errors.answers?.message}
                    </FormMessage>
                  </div>
                ) : (
                  <FormLabel>Answer</FormLabel>
                )}

                {answers.map((answer, answerIndex) => renderQuestionAnswer(answer, answerIndex))}
              </div>
            )}

            {form.watch('type') && hasAnswerControl && !readOnly && (
              <Button
                type="button"
                variant="outline"
                onClick={() =>
                  appendAnswer({ title: '', isCorrect: false, displayOrder: answers.length })
                }
                style={{ alignSelf: 'flex-end' }}
              >
                Add Answer Option
              </Button>
            )}

            {/* Drag and drop */}
            {form.watch('type') === 'Drag and Drop' && (
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                  <span
                    className={cn(
                      'text-sm font-semibold',
                      form.formState.errors.answers && 'text-destructive',
                    )}
                  >
                    Configuration
                  </span>
                  <FormMessage className="text-destructive text-xs">
                    {form.formState.errors.answers?.message}
                  </FormMessage>
                  <p className="text-muted-foreground text-xs">
                    Drag and drop question consist of text blocks and answer blocks. Answer blocks
                    will be shuffled when displayed to the user.
                  </p>

                  {answers.map((block, blockIndex) => {
                    if (!['text', 'answer'].includes(block.type)) return null
                    return (
                      <div className="flex w-full flex-row items-center gap-2" key={block.id}>
                        <FormField
                          key={`${block.id}.block`}
                          control={form.control}
                          name={`answers.${blockIndex}.value`}
                          render={({ field: blockField }) => (
                            <FormItem className="w-full flex-1">
                              <FormControl>
                                <Input
                                  placeholder="Enter text"
                                  {...blockField}
                                  className="flex-1"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                          rules={{
                            required: true,
                          }}
                        />
                        <FormField
                          key={`${block.id}.type`}
                          control={form.control}
                          name={`answers.${blockIndex}.type`}
                          render={({ field: questionField }) => (
                            <FormItem>
                              <FormControl>
                                <Select
                                  id="type"
                                  placeholder="Select Block Type"
                                  options={[
                                    { label: 'Answer', id: 'answer' },
                                    { label: 'Text', id: 'text' },
                                  ]}
                                  {...questionField}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                          rules={{
                            required: true,
                          }}
                        />

                        {blockIndex !== 0 && !readOnly && (
                          <Button
                            variant="plain"
                            type="button"
                            onClick={() => removeAnswer(blockIndex)}
                            className="!p-0"
                          >
                            <CircleX className="text-destructive focus-visible:ring-destructive/20 h-5 w-5" />
                          </Button>
                        )}
                      </div>
                    )
                  })}

                  {!readOnly && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => appendAnswer({ type: 'text', value: '' })}
                      style={{ alignSelf: 'flex-end' }}
                    >
                      Add Block
                    </Button>
                  )}
                </div>

                <div>
                  <span className="text-sm font-semibold">Incorrect answers</span>
                  <p className="text-muted-foreground text-xs">
                    These answers will be shown in addition to the correct answers.
                  </p>
                  <div className="flex flex-col gap-2">
                    {answers.map((block, blockIndex) => {
                      if (block.type !== 'incorrect') return null
                      return (
                        <div className="flex w-full flex-row items-center gap-2" key={block.id}>
                          <FormField
                            key={`${block.id}.value`}
                            control={form.control}
                            name={`answers.${blockIndex}.value`}
                            render={({ field: blockField }) => (
                              <FormItem className="w-full flex-1">
                                <FormControl>
                                  <Input
                                    placeholder="Enter incorrect answer"
                                    {...blockField}
                                    className="flex-1"
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                            rules={{
                              required: true,
                            }}
                          />

                          {!readOnly && (
                            <Button
                              variant="plain"
                              type="button"
                              onClick={() => removeAnswer(blockIndex)}
                              className="!p-0"
                            >
                              <CircleX className="text-destructive focus-visible:ring-destructive/20 h-5 w-5" />
                            </Button>
                          )}
                        </div>
                      )
                    })}
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => appendAnswer({ type: 'incorrect', value: '' })}
                      style={{ alignSelf: 'flex-end' }}
                    >
                      Add Incorrect Answer
                    </Button>
                  </div>
                </div>

                <p className="text-sm font-semibold">Preview</p>
                <span className="text-muted-foreground">
                  {form.watch('answers').map((block, answerIndex) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <Fragment key={`${block.value}-${answerIndex}`}>
                      {block.type === 'text' && block.value}
                      {block.type === 'answer' && (
                        <span className="bg-muted rounded p-2">{block.value}</span>
                      )}{' '}
                    </Fragment>
                  ))}
                </span>
              </div>
            )}
            {form.watch('type') && renderImage()}
          </form>
        </Form>
      }
      title={`${question ? 'Update' : 'Add'} Question`}
      setOpen={close}
    />
  )
}

QuizQuestion.propTypes = {
  append: PropTypes.func.isRequired,
  close: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  question: PropTypes.object,
  readOnly: PropTypes.bool,
  remove: PropTypes.func.isRequired,
  update: PropTypes.func.isRequired,
}

export default QuizQuestion
